import { useCallback, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import log from 'loglevel'
import { ComplianceFramework } from '@trustero/trustero-api-web/lib/audit/framework_pb'
import {
  GetAuditsRequest,
  GetComplianceFrameworksRequest,
} from '@trustero/trustero-api-web/lib/audit/audit_pb'
import { AuditPromiseClient } from '@trustero/trustero-api-web/lib/audit/audit_grpc_web_pb'
import { AccountPromiseClient } from '@trustero/trustero-api-web/lib/account/account_grpc_web_pb'
import { CreateCustomFrameworkRequest } from '@trustero/trustero-api-web/lib/account/account_pb'
import { ModelPromiseClient } from '@trustero/trustero-api-web/lib/model/model_grpc_web_pb'
import {
  AddFrameworkSectionsRequest,
  DeleteFrameworkRequirementRequest,
  DeleteFrameworkSectionRequest,
  EditFrameworkRequirementRequest,
  EditFrameworkSectionRequest,
  FrameworkRequirementState,
  FrameworkSectionState,
} from '@trustero/trustero-api-web/lib/model/framework_pb'
import { ListControlsRequest } from '@trustero/trustero-api-web/lib/model/control_pb'
import { BoolValue } from 'google-protobuf/google/protobuf/wrappers_pb'
import { SearchableData } from 'src/Utils/search/search.types'
import { prepareSearchableData } from 'src/Utils/search/search.helpers'
import { useComplianceFrameworkObjectives } from 'src/components/async/Framework/useComplianceFrameworkObjectives'
import { useFrameworks } from 'src/components/async/Framework/useFrameworks'
import { useFrameworkSections } from 'src/components/async/Framework/useFrameworkSections'
import {
  useControls,
  useInvalidateControlsCache,
} from 'src/components/async/model/control/useControls'
import { useAudits } from 'src/components/async/model/audit/useAudit'
import { useInvalidateComplianceFrameworksCache } from 'src/components/async/complianceframework/useComplianceFrameworks'
import { useGrpcRevalidateByMethod } from 'src/components/async/useGrpcMutate'
import { useAuthorizedGrpcClient } from 'src/adapter'
import { SelectItem } from 'src/components/Reusable/SelectDropdown/SelectDropdown.constants'
import {
  RequirementInfo,
  SectionInfo,
} from 'src/components/ModalForms/Frameworks/FrameworkSectionsEditor.types'
import { getFrameworksFilterRequest } from './frameworks.helpers'

const useInvalidateFrameworkCaches = (): (() => Promise<void>) => {
  const invalidateComplianceFrameworks =
    useInvalidateComplianceFrameworksCache()
  const invalidateControls = useInvalidateControlsCache()
  const revalidateByMethod = useGrpcRevalidateByMethod()

  return useCallback(async () => {
    await Promise.all([
      invalidateComplianceFrameworks(),
      invalidateControls(),
      revalidateByMethod(ModelPromiseClient.prototype.listFrameworks),
      revalidateByMethod(ModelPromiseClient.prototype.listFrameworkSections),
      revalidateByMethod(
        ModelPromiseClient.prototype.getComplianceFrameworkObjectives,
      ),
    ])
  }, [invalidateComplianceFrameworks, invalidateControls, revalidateByMethod])
}

/**
 * Loads the account's controls and pre-derives the shapes the framework modals
 * consume:
 *  - `controlOptions`: SelectItem[] for dropdowns. Values are control_models.id
 *    (XIDs) — what the rpcs expect.
 *  - `validControlModelIdSet`: Set<model_id> for CSV mapping validation. CSV
 *    authors write human-readable model_ids; we still validate against those.
 *  - `controlIdByModelId`: Map<model_id, XID> so the CSV path can translate
 *    parsed model_ids into the XIDs that SectionInfo.controlIds carries.
 */
export const useFrameworkControls = (
  enabled: boolean,
): {
  controlOptions: SelectItem[]
  validControlModelIdSet: Set<string>
  controlIdByModelId: Map<string, string>
  isLoading: boolean
} => {
  const { data: controlsData, isLoading } = useControls(
    new ListControlsRequest(),
    enabled,
  )
  const controls = useMemo(
    () => controlsData?.getItemsList() ?? [],
    [controlsData],
  )
  const controlOptions = useMemo<SelectItem[]>(
    () =>
      controls.map((c) => ({
        name: `${c.getModelId()} ${c.getName()}`,
        value: c.getId(),
      })),
    [controls],
  )
  const validControlModelIdSet = useMemo(
    () => new Set(controls.map((c) => c.getModelId())),
    [controls],
  )
  const controlIdByModelId = useMemo(
    () => new Map(controls.map((c) => [c.getModelId(), c.getId()])),
    [controls],
  )
  return {
    controlOptions,
    validControlModelIdSet,
    controlIdByModelId,
    isLoading,
  }
}

/**
 * Loads section + requirement names already in a framework
 */
export const useExistingFrameworkNames = (
  framework: ComplianceFramework,
  show: boolean,
): {
  existingSectionNames: string[]
  existingRequirementNames: string[]
  isLoadingSections: boolean
} => {
  const cfModelId = framework.getModelId()
  const { data: sectionsData, isLoading: isLoadingSections } =
    useFrameworkSections(cfModelId, show)
  const { data: requirementsData } = useComplianceFrameworkObjectives([
    framework.getId(),
  ])

  const existingSectionNames = useMemo(
    () => (sectionsData?.getSectionsList() ?? []).map((s) => s.getName()),
    [sectionsData],
  )
  const existingRequirementNames = useMemo(
    () => (requirementsData?.getItemsList() ?? []).map((r) => r.getName()),
    [requirementsData],
  )

  return {
    existingSectionNames,
    existingRequirementNames,
    isLoadingSections,
  }
}

// Builds the GetComplianceFrameworksRequest from the current URL
export const useFrameworksRequest = (): GetComplianceFrameworksRequest => {
  const location = useLocation()
  return useMemo(
    () => getFrameworksFilterRequest(location.search),
    [location.search],
  )
}

export const useSearchableFrameworks = (
  frameworks: ComplianceFramework[],
): SearchableData<ComplianceFramework>[] =>
  useMemo(
    () =>
      prepareSearchableData(
        frameworks,
        (framework: ComplianceFramework) => framework.getId(),
        (framework: ComplianceFramework) => [
          framework.getName(),
          framework.getDescription(),
        ],
      ),
    [frameworks],
  )

type FrameworkMetadataCounts = {
  requirementsByFrameworkId: Record<string, number>
  controlsByFrameworkId: Record<string, number>
  auditsByFrameworkId: Record<string, number>
  isLoading: boolean
}

export const useFrameworkMetadataCounts = (): FrameworkMetadataCounts => {
  const { data: frameworksData, isLoading: frameworksLoading } = useFrameworks()
  const { data: controlsData, isLoading: controlsLoading } = useControls(
    undefined,
    true,
    undefined,
    undefined,
    false,
    true,
  )
  const openAuditsRequest = useMemo(
    () => new GetAuditsRequest().setIsClosed(new BoolValue().setValue(false)),
    [],
  )
  const { data: auditsData, isLoading: auditsLoading } =
    useAudits(openAuditsRequest)

  const requirementsByFrameworkId = useMemo(() => {
    const counts: Record<string, number> = {}
    frameworksData?.getItemsList().forEach((framework) => {
      const cfId = framework.getComplianceFrameworkId()
      if (cfId) counts[cfId] = (counts[cfId] ?? 0) + 1
    })
    return counts
  }, [frameworksData])

  const controlsByFrameworkId = useMemo(() => {
    const counts: Record<string, number> = {}
    controlsData?.getItemsList().forEach((control) => {
      control.getComplianceFrameworkIdsList().forEach((cfId) => {
        counts[cfId] = (counts[cfId] ?? 0) + 1
      })
    })
    return counts
  }, [controlsData])

  const auditsByFrameworkId = useMemo(() => {
    const counts: Record<string, number> = {}
    auditsData?.getItemsList().forEach((audit) => {
      audit.getComplianceFrameworksList().forEach((cf) => {
        const cfId = cf.getId()
        if (cfId) counts[cfId] = (counts[cfId] ?? 0) + 1
      })
    })
    return counts
  }, [auditsData])

  return {
    requirementsByFrameworkId,
    controlsByFrameworkId,
    auditsByFrameworkId,
    isLoading: frameworksLoading || controlsLoading || auditsLoading,
  }
}

const sectionDraftToProto = (s: SectionInfo): FrameworkSectionState => {
  const out = new FrameworkSectionState()
    .setName(s.name.trim())
    .setDetails(s.description.trim())
  if (s.modelId) out.setModelId(s.modelId)
  out.setRequirementsList(
    s.requirements
      .filter((r) => r.name.trim().length > 0)
      .map((r) => requirementDraftToProto(r)),
  )
  return out
}

const requirementDraftToProto = (
  r: RequirementInfo,
): FrameworkRequirementState => {
  const req = new FrameworkRequirementState()
    .setName(r.name.trim())
    .setDescription(r.description.trim())
    .setControlIdsList(r.controlIds)
  if (r.modelId) req.setModelId(r.modelId)
  return req
}

export type AddFrameworkSectionsArgs = {
  complianceFrameworkModelId: string
  sections: SectionInfo[]
}

/**
 * Appends one or more new sections (with their nested requirements + control
 * mappings) to an existing custom framework.
 */
export const useAddFrameworkSections = (): ((
  args: AddFrameworkSectionsArgs,
) => Promise<void>) => {
  const modelClient = useAuthorizedGrpcClient(ModelPromiseClient)
  const invalidateFrameworkCaches = useInvalidateFrameworkCaches()

  return useCallback(
    async ({ complianceFrameworkModelId, sections }) => {
      const filtered = sections.filter((s) => s.name.trim().length > 0)
      if (filtered.length === 0) return
      const req = new AddFrameworkSectionsRequest()
        .setComplianceFrameworkModelId(complianceFrameworkModelId)
        .setSectionsList(
          filtered.map((s) => {
            const proto = sectionDraftToProto(s)
            // Add path: never echo a model_id back to the server.
            proto.setModelId('')
            proto.getRequirementsList().forEach((r) => r.setModelId(''))
            return proto
          }),
        )
      await modelClient.addFrameworkSections(req)
      await invalidateFrameworkCaches()
    },
    [modelClient, invalidateFrameworkCaches],
  )
}

export type EditFrameworkSectionArgs = {
  sectionModelId: string
  name: string
  details: string
  requirements: RequirementInfo[]
}

/**
 * Replaces one section's name/details plus its full requirements list
 */
export const useEditFrameworkSection = (): ((
  args: EditFrameworkSectionArgs,
) => Promise<void>) => {
  const modelClient = useAuthorizedGrpcClient(ModelPromiseClient)
  const invalidateFrameworkCaches = useInvalidateFrameworkCaches()

  return useCallback(
    async ({ sectionModelId, name, details, requirements }) => {
      const req = new EditFrameworkSectionRequest()
        .setSectionModelId(sectionModelId)
        .setName(name.trim())
        .setDetails(details.trim())
        .setRequirementsList(
          requirements
            .filter((r) => r.name.trim().length > 0)
            .map((r) => requirementDraftToProto(r)),
        )
      await modelClient.editFrameworkSection(req)
      await invalidateFrameworkCaches()
    },
    [modelClient, invalidateFrameworkCaches],
  )
}

export const useDeleteFrameworkSection = (): ((
  sectionModelId: string,
) => Promise<void>) => {
  const modelClient = useAuthorizedGrpcClient(ModelPromiseClient)
  const invalidateFrameworkCaches = useInvalidateFrameworkCaches()

  return useCallback(
    async (sectionModelId: string) => {
      await modelClient.deleteFrameworkSection(
        new DeleteFrameworkSectionRequest().setSectionModelId(sectionModelId),
      )
      await invalidateFrameworkCaches()
    },
    [modelClient, invalidateFrameworkCaches],
  )
}

export type EditFrameworkRequirementArgs = {
  requirementModelId: string
  name: string
  description: string
  controlIds: string[]
}

/**
 * Updates one requirement's name, description, and control mappings
 */
export const useEditFrameworkRequirement = (): ((
  args: EditFrameworkRequirementArgs,
) => Promise<void>) => {
  const modelClient = useAuthorizedGrpcClient(ModelPromiseClient)
  const invalidateFrameworkCaches = useInvalidateFrameworkCaches()

  return useCallback(
    async ({ requirementModelId, name, description, controlIds }) => {
      const req = new EditFrameworkRequirementRequest()
        .setRequirementModelId(requirementModelId)
        .setName(name.trim())
        .setDescription(description.trim())
        .setControlIdsList(controlIds)
      await modelClient.editFrameworkRequirement(req)
      await invalidateFrameworkCaches()
    },
    [modelClient, invalidateFrameworkCaches],
  )
}

/**
 * Removes one requirement and cleans up its control linkages
 */
export const useDeleteFrameworkRequirement = (): ((
  requirementModelId: string,
) => Promise<void>) => {
  const modelClient = useAuthorizedGrpcClient(ModelPromiseClient)
  const invalidateFrameworkCaches = useInvalidateFrameworkCaches()

  return useCallback(
    async (requirementModelId: string) => {
      await modelClient.deleteFrameworkRequirement(
        new DeleteFrameworkRequirementRequest().setRequirementModelId(
          requirementModelId,
        ),
      )
      await invalidateFrameworkCaches()
    },
    [modelClient, invalidateFrameworkCaches],
  )
}

export type CreateCustomFrameworkArgs = {
  name: string
  description?: string
}

export type CreateCustomFrameworkResult = {
  complianceFrameworkModelId: string
  complianceFrameworkRecordId: string
}

/**
 * Creates an empty custom framework
 */
export const useCreateCustomFramework = (): ((
  args: CreateCustomFrameworkArgs,
) => Promise<CreateCustomFrameworkResult>) => {
  const accountClient = useAuthorizedGrpcClient(AccountPromiseClient)
  const auditClient = useAuthorizedGrpcClient(AuditPromiseClient)
  const invalidateFrameworkCaches = useInvalidateFrameworkCaches()

  return useCallback(
    async ({ name, description }) => {
      const response = await accountClient.createCustomFramework(
        new CreateCustomFrameworkRequest()
          .setName(name)
          .setDescription(description ?? ''),
      )
      const complianceFrameworkModelId = response.getModelId()
      await invalidateFrameworkCaches()

      const refreshed = await auditClient.getComplianceFrameworks(
        new GetComplianceFrameworksRequest(),
      )
      const recordId = refreshed
        .getItemsList()
        .find((f) => f.getModelId() === complianceFrameworkModelId)
        ?.getId()
      if (!recordId) {
        log.error(
          'Created framework but could not resolve record id for navigation',
          { complianceFrameworkModelId },
        )
        throw new Error(
          'Framework was created but could not be loaded. Please refresh.',
        )
      }
      return {
        complianceFrameworkModelId,
        complianceFrameworkRecordId: recordId,
      }
    },
    [accountClient, auditClient, invalidateFrameworkCaches],
  )
}
